import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translations from '@translationKeys'

import { ActionsContainer } from '@components/ConfirmPopover'
import SelectWithLabeledValue from '@components/SelectWithLabeledValue'
import { SelectOption } from '@components/SelectWithLabeledValue/index.types'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { PendoImportProspectsEventType } from '@modules/pendo/types'
import { CompanyParticipant } from '@modules/shortlist/types'
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown'
import { Box, Button, Divider, Typography } from '@mui/material'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'

import FilterSection from './FilterSection'
import ProspectSection from './ProspectSection'
import {
  IMPORT_PROSPECT_SUBTITLES_MAP,
  IMPORT_PROSPECT_TITLES_MAP,
  PROSPECT_STATUS_LABELS_MAP,
  PROSPECT_STATUS_ORDER,
} from './constant'
import {
  CustomButton,
  ImportProspectSubTitle,
  ImportProspectsBottomBox,
  ImportProspectsHeadBox,
  ImportProspectsScrollContainer,
  ImportProspectsTitle,
  ProspectFiltersBox,
  ProspectsContainerBox,
  ProspectsSelectionViewWrapper,
} from './index.styles'
import { ImportProspect, ImportProspectsRequestTypes, ImportProspectsStatus, ImportProspectsView } from './types'

interface ProspectsSelectionViewProps {
  filters: {
    title: string
    key: string
    options: { label: string; id: number | ImportProspectsRequestTypes | ImportProspectsStatus; color?: string }[]
  }[]
  participants: CompanyParticipant[]
  selectedIds: string[]
  handleFilterChange: (id: string) => void
  handleMultipleFiltersChange: (ids: string[]) => void
  onCancel: () => void
  onConfirm: () => void
  matchingProspectsCount?: number
  prospects?: { companies: ImportProspect[]; status: ImportProspectsStatus }[]
  selectedProspectIds?: number[]
  onProspectSelect: (id: number) => void
  onToggleSelectAllProspects: () => void
  onSelectAllProspects: () => void
  selectedParticipantId?: number
  onSelectParticipant: (value: number) => void
  onSendPendoEvent: (data: { eventType: PendoImportProspectsEventType }) => void
}

const ProspectsSelectionView = (props: ProspectsSelectionViewProps) => {
  const {
    filters,
    participants,
    selectedIds,
    matchingProspectsCount,
    prospects,
    selectedProspectIds,
    selectedParticipantId,
    onProspectSelect,
    onToggleSelectAllProspects,
    onSelectAllProspects,
    handleFilterChange,
    handleMultipleFiltersChange,
    onCancel,
    onConfirm,
    onSelectParticipant,
    onSendPendoEvent,
  } = props
  const { t } = useTranslation()
  const { user } = useAuthContext()
  const { isMobile } = useScreenDimensions()
  const [hasScrollbar, setHasScrollbar] = useState(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const { canImportParticipants } = useFeatureFlags()
  const sanitizedProspects = useMemo(
    () => PROSPECT_STATUS_ORDER.map(status => prospects?.find(prospect => prospect.status === status)).filter(Boolean),
    [prospects],
  )

  const [isProspectsOpen, setIsProspectsOpen] = useState<boolean>(false)

  const handleProspectsContainerClick = () => {
    onSendPendoEvent({ eventType: PendoImportProspectsEventType.MainPopoverSelectSpecificClick })
    setIsProspectsOpen(true)
  }

  const importProspectsButtonText = useMemo(() => {
    if (matchingProspectsCount && matchingProspectsCount > 1) {
      return t(translations.IMPORT_ALL_COUNT_PROSPECT, { count: matchingProspectsCount })
    }
    if (matchingProspectsCount === 1) {
      return t(translations.IMPORT_COUNT_PROSPECT, { count: matchingProspectsCount })
    }
    return t(translations.IMPORT)
  }, [matchingProspectsCount, t])

  useEffect(() => {
    if (scrollRef.current) {
      const isScrollbarVisible = scrollRef.current.scrollHeight > scrollRef.current.clientHeight
      setHasScrollbar(isScrollbarVisible)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRef.current])

  const participantOptions = useMemo(() => {
    const currentUser = participants.find(delegate => delegate.userId === user?.id)
    const mappedParticipants: SelectOption[] = participants
      .filter(delegate => delegate.userId !== user?.id)
      .sort((delegate1, delegate2) => delegate1.lastName.localeCompare(delegate2.lastName))
      .map(participant => ({
        label: `${participant.firstName} ${participant.lastName}`,
        id: participant.id,
      }))

    if (currentUser) {
      mappedParticipants.unshift({
        label: `${currentUser.firstName} ${currentUser.lastName}`,
        id: currentUser.id,
        divider: !!mappedParticipants.length,
      })
    }

    return mappedParticipants
  }, [participants, user])

  const renderSelectedState = useCallback(() => {
    if (!selectedParticipantId) {
      return <Typography variant="body1">{t(translations.ALL_LABEL)}</Typography>
    }

    return (
      <Typography variant="body1" width="100%" overflow="hidden" textOverflow="ellipsis">
        {participantOptions.find(participant => participant.id === selectedParticipantId)?.label ?? t(translations.ALL_LABEL)}
      </Typography>
    )
  }, [participantOptions, selectedParticipantId, t])

  const handleImportAllConfirm = () => {
    onSendPendoEvent({ eventType: PendoImportProspectsEventType.MainPopoverImportClick })
    onSelectAllProspects()
    onConfirm()
  }

  const handleImportSpecificConfirm = () => {
    onSendPendoEvent({ eventType: PendoImportProspectsEventType.SelectSpecificImportClick })
    onConfirm()
  }

  return (
    <ProspectsSelectionViewWrapper>
      {(!isMobile || !isProspectsOpen) && (
        <ProspectFiltersBox>
          <ImportProspectsHeadBox>
            <ImportProspectsTitle variant="h2">
              {t(IMPORT_PROSPECT_TITLES_MAP[ImportProspectsView.prospect])}
            </ImportProspectsTitle>

            <ImportProspectSubTitle variant="body1">
              {t(IMPORT_PROSPECT_SUBTITLES_MAP[ImportProspectsView.prospect] as string)}
            </ImportProspectSubTitle>
          </ImportProspectsHeadBox>
          <Divider sx={{ margin: '16px 0' }} />

          <ImportProspectsScrollContainer $withScrollBar={hasScrollbar} ref={scrollRef}>
            {canImportParticipants ? (
              <Box mb="10px">
                <Typography variant="caption">{t(translations.SELECT_PARTICIPANT)}</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <SelectWithLabeledValue
                  onSelect={onSelectParticipant}
                  label={t(translations.ALL_LABEL)}
                  options={participantOptions}
                  emptySelection
                  emptySelectionLabel={t(translations.ALL_LABEL)}
                  selectedOptionId={selectedParticipantId}
                  renderSelectedState={renderSelectedState}
                  width="100%"
                  maxListHeight="220px"
                />
              </Box>
            ) : null}
            {filters
              .filter(f => f.options?.length)
              .map(i => (
                <FilterSection
                  key={i.key}
                  title={t(i.title)}
                  options={i.options.map(op => ({ ...op, id: op.id as string, label: t(op.label) }))}
                  selectedIds={selectedIds}
                  handleSelect={handleFilterChange}
                  handleSelectAll={handleMultipleFiltersChange}
                />
              ))}
          </ImportProspectsScrollContainer>

          <Divider sx={{ margin: '10px 0' }} />

          <ImportProspectsBottomBox>
            <ActionsContainer sx={{ marginBottom: '10px', flex: '0 0 auto' }}>
              <Button variant="outlined" onClick={onCancel} sx={{ marginRight: '10px' }}>
                {t(translations.CANCEL_BUTTON)}
              </Button>
              <Button
                variant="contained"
                onClick={handleImportAllConfirm}
                disabled={!selectedIds?.length || !matchingProspectsCount}
              >
                {importProspectsButtonText}
              </Button>
            </ActionsContainer>

            {!!selectedIds?.length && !!matchingProspectsCount && (
              <Box display="flex" alignItems="center" justifyContent="flex-end">
                <CustomButton
                  variant="contained"
                  onClick={handleProspectsContainerClick}
                  endIcon={
                    <Box
                      sx={{
                        fontSize: '31px',
                        transform: 'rotate(-90deg)',
                      }}
                    >
                      <ExpandCircleDownIcon color="primary" />
                    </Box>
                  }
                >
                  {t(translations.SELECT_SPECIFIC_PROSPECTS)}
                </CustomButton>
              </Box>
            )}
          </ImportProspectsBottomBox>
        </ProspectFiltersBox>
      )}
      {isProspectsOpen && !!selectedIds?.length && !!sanitizedProspects?.length && (
        <ProspectsContainerBox>
          <FilterSection
            title=""
            options={[{ id: 'all', label: t(translations.SELECT_ALL_LABEL) }]}
            selectedIds={!!selectedProspectIds?.length && selectedProspectIds?.length === matchingProspectsCount ? ['all'] : []}
            handleSelect={onToggleSelectAllProspects}
          />
          <Divider sx={{ margin: '10px 0' }} />
          {sanitizedProspects.map(pr => (
            <ProspectSection
              key={pr?.status}
              title={t(PROSPECT_STATUS_LABELS_MAP[pr?.status as string] || '')}
              options={pr?.companies.map(c => ({ id: String(c.id), label: c.name, tags: c.tags })) || []}
              selectedIds={selectedProspectIds?.map(String) || []}
              handleSelect={(id: string) => onProspectSelect(Number(id))}
            />
          ))}
          <Box display="flex" justifyContent="end" gap="5px">
            <Button variant="outlined" onClick={() => setIsProspectsOpen(false)}>
              {t(translations.COMMON_GO_BACK)}
            </Button>

            <Button
              variant="contained"
              onClick={handleImportSpecificConfirm}
              disabled={!selectedProspectIds?.length || !matchingProspectsCount}
            >
              {selectedProspectIds?.length
                ? t(translations.IMPORT_COUNT_PROSPECT, { count: selectedProspectIds.length })
                : t(translations.IMPORT)}
            </Button>
          </Box>
        </ProspectsContainerBox>
      )}
    </ProspectsSelectionViewWrapper>
  )
}

export default ProspectsSelectionView
